/**
 * Softening the crease's edge — ADR-028 E3's two missing halves.
 *
 * THE DEFECT THIS FIXES. `deriveCreaseDeltas` measures what LBS got wrong
 * inside a band and emits a morph for exactly those vertices. Its own delta
 * smoothing pass then averages the field "WITHIN THE BAND ONLY ... never
 * against the zeros outside" — which is correct for what it is, and is also
 * precisely the hard edge a person sees: the correction stops dead at the band
 * boundary, and where a slender limb makes the correction large, the boundary
 * reads as a crease of its own and the outside of the joint pokes.
 *
 * TWO PASSES, ALWAYS BOTH. Allen's field verdict was that neither alone is
 * enough and the pair is right on every case he tested, so there is no method
 * selector here — the caller asks for softening or does not.
 *
 *   1. extendCorrection   feather the correction field OUTWARD by `rings`,
 *                         distance-weighted, so it decays into the flesh
 *                         instead of ending at a line.
 *   2. repairOuterSpikes  the feathering raises outer points; this measures
 *                         each against the surface it sat in at REST and
 *                         presses the new ones back down along the
 *                         neighbourhood normal, under three separate budgets.
 *
 * PROVENANCE. Both are Astra's, from the `investigate/slender-elbows` study,
 * ported verbatim from `slender-elbow-experiment.ts`. In the study they ran at
 * RENDER time on a temporary mesh and could never reach World; here they run at
 * DERIVE time on the MORPH'S OWN APPLIED CONTRIBUTION — `posed` with the
 * derived deltas skinned in, against `posed` without them, exactly the study's
 * `current` against `base` — so the result folds back into an ordinary morph
 * target that World plays with no runtime support at all. Nothing downstream
 * of this file knows the softening happened.
 *
 * NOT the mush reference. That was the first wiring, and it measured as a
 * no-op: the reference has no spikes to press and no edge to feather, because
 * "mush differs from LBS a little everywhere". The spikes are the morph's.
 *
 * SEAMS. The study welded with its own tolerance; this welds with the DELTA
 * MUSH's graph, so the softening and the reference it modifies agree about
 * which vertices are the same point. Displacement is scattered, never absolute
 * position, so a pre-existing seam offset survives.
 *
 * Depends on: three, spike-metrics, mesh-smoothing (WeldedGraph). Depended on
 * by: derive-crease-deltas.
 */
import { Vector3 } from 'three';
import type { WeldedGraph } from './mesh-smoothing.js';
import { measureSpikes, median, spikeTopology, type SpikeTopology } from './spike-metrics.js';

const smooth = (a: number, b: number, x: number): number => {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/**
 * Feather the correction field outward. The field starts as `corrected−base`
 * wherever that is non-zero; each ring takes a distance-weighted average of
 * whatever its neighbours already carry, scaled by a term that decays with the
 * ring index, and only ever writes into `allowed`.
 */
export function extendCorrection(
  topology: SpikeTopology,
  base: Vector3[],
  corrected: Vector3[],
  allowed: Set<number>,
  rings = 2,
  /**
   * Which vertices SEED the field. Measured, and the reason this parameter
   * exists at all: in the study `corrected` was the corrective stack's output,
   * which is confined to the band by construction, so the field had an edge to
   * feather across. Here `corrected` is the DELTA MUSH reference, and "mush
   * differs from LBS a little everywhere" (derive-crease-deltas' own words) —
   * so seeding on non-zero put EVERY vertex in the field, left no candidate
   * outside it, and the feather extended exactly 0 vertices on the goblin.
   * The seed must be the band, so that a boundary exists.
   */
  seed?: Set<number>,
): { positions: Vector3[]; extended: number; seeded: number } {
  const field = new Map<number, Vector3>();
  for (let v = 0; v < base.length; v++) {
    if (seed !== undefined && !seed.has(v)) continue;
    const d = corrected[v]!.clone().sub(base[v]!);
    if (d.lengthSq() > 1e-18) field.set(v, d);
  }
  const seeded = field.size;
  let frontier = [...field.keys()];
  for (let ring = 1; ring <= rings; ring++) {
    const candidates = new Set<number>();
    for (const v of frontier)
      for (const n of topology.neighbors[v]!)
        if (allowed.has(n) && !field.has(n)) candidates.add(n);
    const next = new Map<number, Vector3>();
    for (const v of candidates) {
      let weight = 0;
      const d = new Vector3();
      for (const n of topology.neighbors[v]!) {
        const dn = field.get(n);
        if (!dn) continue;
        const w = 1 / Math.max(1e-9, topology.rest[v]!.distanceTo(topology.rest[n]!));
        d.addScaledVector(dn, w);
        weight += w;
      }
      if (weight > 0)
        next.set(v, d.multiplyScalar((rings + 1 - ring) / (rings + 2 - ring) / weight));
    }
    for (const [v, d] of next) field.set(v, d);
    frontier = [...next.keys()];
  }
  return {
    positions: base.map((p, v) => p.clone().add(field.get(v) ?? new Vector3())),
    extended: field.size - seeded,
    seeded,
  };
}

/**
 * The oriented-area normal of a vertex's incident fan, and how far the vertex
 * stands off its ring's centroid along it. For a CLOSED fan the centre
 * vertex's own contribution cancels, so a spike cannot tip the normal it is
 * about to be measured against — which is the whole reason this is measured
 * from face areas rather than from the vertex normal.
 */
function ringHeight(topology: SpikeTopology, positions: Vector3[], v: number) {
  const ns = [...topology.neighbors[v]!];
  const center = ns
    .reduce((p, n) => p.add(positions[n]!), new Vector3())
    .multiplyScalar(1 / ns.length);
  const normal = new Vector3();
  for (const f of topology.faces[v]!) {
    const a = positions[topology.triangles[f]!]!,
      b = positions[topology.triangles[f + 1]!]!,
      c = positions[topology.triangles[f + 2]!]!;
    normal.add(b.clone().sub(a).cross(c.clone().sub(a)));
  }
  normal.normalize();
  return { normal, height: positions[v]!.clone().sub(center).dot(normal) };
}

/**
 * Press newly raised outer points back down. Every term is a budget:
 *
 *   `excess`    only the height ABOVE what the vertex already had at rest —
 *               a shape that was always proud stays proud
 *   `gain`      four smoothsteps that must ALL agree it is a new, local,
 *               newly-sharp outlier before any of it is applied
 *   `remaining` a hard travel cap per vertex across all passes, so the repair
 *               can never walk a vertex away from where the reference put it
 */
export function repairOuterSpikes(
  topology: SpikeTopology,
  input: Vector3[],
  outer: Set<number>,
  radius: number,
  passes = 3,
): { positions: Vector3[]; changed: number[] } {
  let positions = input.map((p) => p.clone());
  const changed = new Set<number>();
  for (let pass = 0; pass < passes; pass++) {
    const readings = measureSpikes(topology, positions).rows;
    const next = positions.map((p) => p.clone());
    for (const r of readings) {
      const v = r.vertex;
      if (!outer.has(v) || !r.closed) continue;
      const scale = median(
        [...topology.neighbors[v]!].map((n) => topology.rest[v]!.distanceTo(topology.rest[n]!)),
      );
      const reference = ringHeight(topology, topology.rest, v),
        current = ringHeight(topology, positions, v);
      const excess = Math.max(0, current.height - Math.max(0, reference.height));
      const gain =
        smooth(0.35, 0.9, r.excessInEdgeLengths) *
        smooth(1.5, 3, r.neighborContrast) *
        smooth(10, 40, r.posedFaceAngle - r.restFaceAngle) *
        smooth(0.1, 0.4, excess / scale);
      const remaining = Math.max(
        0,
        Math.min(scale * 0.8, radius * 0.15) - positions[v]!.distanceTo(input[v]!),
      );
      const amount = Math.min(excess * 0.65, scale * 0.4, remaining) * gain;
      if (amount > 1e-10) {
        next[v]!.addScaledVector(current.normal, -amount);
        changed.add(v);
      }
    }
    positions = next;
  }
  return { positions, changed: [...changed] };
}

export interface SoftenRegions {
  /** Vertices the correction may feather INTO — the study's `bandWorld × 3`, limb flesh only. */
  readonly allowed: Set<number>;
  /** Vertices the spike repair may press down — the study's outside-of-the-joint half of the band. */
  readonly outer: Set<number>;
  /** Vertices the spike metrics are computed over — the study's `bandWorld × 1.75`. */
  readonly selected: Set<number>;
  /** The limb's radius through the band, used as the repair's travel budget. */
  readonly radius: number;
}

export interface SoftenReport {
  /** Vertices that received correction they had none of — the feather. */
  readonly extended: number;
  /** Vertices the spike repair actually moved. */
  readonly despiked: number;
  /**
   * Nodes that actually SEEDED the field — band nodes carrying a non-zero
   * morph contribution. NOT the size of `selected` (1.75x the band): reporting
   * that inflated every marker and made the guard compare two region sizes
   * instead of reach against field (Codex P2, #167). Zero means nothing to feather.
   */
  readonly seeded: number;
  /** Nodes the feather was permitted to reach. */
  readonly allowed: number;
  readonly rings: number;
  readonly passes: number;
}

/**
 * Run both passes over a flat posed/target pair and hand back a new target.
 *
 * Welded copies enter ONCE and leave as the same displacement (the gather /
 * scatter convention `smoothWelded` established), so a UV seam cannot crack
 * and cannot be weighted by how many copies it happens to have.
 */
export function softenCorrection(
  graph: WeldedGraph,
  restPositions: ArrayLike<number>,
  index: ArrayLike<number> | null,
  posed: ArrayLike<number>,
  target: ArrayLike<number>,
  regions: SoftenRegions,
  rings = 2,
  passes = 3,
): { positions: Float32Array; report: SoftenReport } {
  const vertexCount = restPositions.length / 3;
  const { weldOf, nodeCount } = graph;

  // ── gather to nodes: first copy seen wins, exactly as smoothWelded does ──
  const rest: Vector3[] = Array.from({ length: nodeCount }, () => new Vector3());
  const base: Vector3[] = Array.from({ length: nodeCount }, () => new Vector3());
  const corrected: Vector3[] = Array.from({ length: nodeCount }, () => new Vector3());
  const seen = new Uint8Array(nodeCount);
  for (let v = 0; v < vertexCount; v += 1) {
    const n = weldOf[v]!;
    if (seen[n] === 1) continue;
    seen[n] = 1;
    rest[n]!.set(restPositions[v * 3]!, restPositions[v * 3 + 1]!, restPositions[v * 3 + 2]!);
    base[n]!.set(posed[v * 3]!, posed[v * 3 + 1]!, posed[v * 3 + 2]!);
    corrected[n]!.set(target[v * 3]!, target[v * 3 + 1]!, target[v * 3 + 2]!);
  }

  // Triangles and regions move to node ids too, so neighbourhoods are welded
  // neighbourhoods and a seam is one vertex rather than two coincident ones.
  const triangleCount = index === null ? vertexCount : index.length;
  const triangles = new Int32Array(triangleCount);
  for (let i = 0; i < triangleCount; i += 1) triangles[i] = weldOf[index === null ? i : index[i]!]!;
  const toNodes = (vs: Set<number>): Set<number> => {
    const out = new Set<number>();
    for (const v of vs) out.add(weldOf[v]!);
    return out;
  };

  const selectedNodes = toNodes(regions.selected);
  const topology = spikeTopology(rest, triangles, selectedNodes);
  const allowedNodes = toNodes(regions.allowed);
  const extendResult = extendCorrection(topology, base, corrected, allowedNodes, rings, selectedNodes);
  const repairResult = repairOuterSpikes(
    topology,
    extendResult.positions,
    toNodes(regions.outer),
    regions.radius,
    passes,
  );

  // ── scatter back: the DISPLACEMENT from the original target, never the
  // absolute position, so a seam's pre-existing offset survives untouched ──
  const out = new Float32Array(restPositions.length);
  for (let v = 0; v < vertexCount; v += 1) {
    const n = weldOf[v]!;
    out[v * 3] = target[v * 3]! + (repairResult.positions[n]!.x - corrected[n]!.x);
    out[v * 3 + 1] = target[v * 3 + 1]! + (repairResult.positions[n]!.y - corrected[n]!.y);
    out[v * 3 + 2] = target[v * 3 + 2]! + (repairResult.positions[n]!.z - corrected[n]!.z);
  }
  return {
    positions: out,
    report: {
      seeded: extendResult.seeded,
      allowed: allowedNodes.size,
      extended: extendResult.extended,
      despiked: repairResult.changed.length,
      rings,
      passes,
    },
  };
}
